import { useEffect } from 'react';

import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';

import { useDocumentUploadStepContext } from '../context/documentUploadStep.context';
import { usePersistUploadedDocuments } from '../hooks/usePersistUploadedDocuments';

/**
 * Invisible, session-scoped worker that saves uploaded documents in the
 * background. Once the user has committed (pressed Continue or an exit action),
 * it persists any not-yet-saved files whenever uploads settle — so a file still
 * uploading when the user advanced past the step still becomes a Document.
 *
 * It renders nothing and relies on the per-file claim in
 * {@link usePersistUploadedDocuments} to avoid duplicating files the committing
 * action already saved.
 */
export function DocumentBackgroundPersister() {
  const { isUploading } = useMultiDocumentUploaderContext();
  const { hasCommitted } = useDocumentUploadStepContext();
  const { persist } = usePersistUploadedDocuments();

  // `persist`'s identity changes when the uploaded-file set does, so this also
  // re-runs as each upload settles — persisting the newly-landed files (a no-op
  // when there's nothing new to save).
  useEffect(() => {
    if (!isUploading && hasCommitted()) {
      void persist();
    }
  }, [isUploading, hasCommitted, persist]);

  return null;
}
